import React, { useState } from 'react'
import { PageSection, Title } from '@patternfly/react-core'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'design-react-kit'
import GenericBreadCrumb from 'src/frontend/app/components/GenericBreadCrumb'
import GenericInput from 'src/frontend/app/components/Form/Input'
import { GenericAsyncTableData, RequestData } from 'src/backoffice/app/components/GenericAsyncTableData'
import {
  PATH_TO_BO_LISTA_ISCRITTI_ORDINE,
  PATH_TO_BO_LISTA_PROROGHE,
  PATH_TO_BO_RIEPILOGO_RIPARTIZIONI,
  PATH_TO_FO_RIEPILOGO_RIPARTIZIONI,
} from 'src/app/utils/RoutesConstants'
import { TRIENNI_OPTIONS } from './CreaProroga'
import { getDefaultAxiosConfig, getFormazioneResourceApi } from 'src/app/utils/ManagerRestGateway'
import { TokenStore } from 'src/keycloak/jwt/TokenStore'
import { generateRequestFilter, generateRequestSort } from 'src/app/utils/ApiUtils'

const Spacer = () => <br />

const getTriennioLabel = (value: any) => TRIENNI_OPTIONS.find((t) => t.value === value)?.label || value

const formatDate = (date?: string) => {
  if (!date) return '-'

  const [year, month, day] = date.split('-')
  return `${day}-${month}-${year}`
}

const ListaProroghe = (props: any) => {
  const searchParams = new URLSearchParams(window.location.search)
  const cf = searchParams.get('cf')
  const isUser = TokenStore.getInstance().isCurrentAccountIscrittoOrdine()
  const nascondiAction = isUser || !cf

  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
  const [selectedDeroga, setSelectedDeroga] = React.useState<any>(null)

  const [showEditModal, setShowEditModal] = React.useState(false)
  const [selectedEdit, setSelectedEdit] = React.useState<any>(null)

  const [editError, setEditError] = React.useState<string | null>(null)

  const [deleteMode, setDeleteMode] = React.useState<'confirm' | 'provvedimento'>('confirm')
  const [provvedimentoText, setProvvedimentoText] = React.useState('')

  const [editData, setEditData] = React.useState({
    dataInizioDeroga: '',
    dataFineDeroga: '',
    provvedimento: '',
  })

  const [loadingEdit, setLoadingEdit] = React.useState(false)

  const isEditInvalid = !editData.dataInizioDeroga || !editData.dataFineDeroga || !editData.provvedimento

  const [resultModal, setResultModal] = React.useState<any>({
    open: false,
    title: '',
    message: { head: '', data: [] },
    ok: false,
  })
  const [loadingDelete, setLoadingDelete] = React.useState(false)
  const [refreshKey, setRefreshKey] = React.useState(0)

  const breadcrumbOptions = [
    {
      label: 'Concessione proroghe',
      link: PATH_TO_BO_LISTA_ISCRITTI_ORDINE,
    },
    {
      label: 'Lista proroghe di ' + cf,
      link: PATH_TO_BO_LISTA_PROROGHE,
    },
  ]

  const loadData = async () => {
    if (!cf) return { items: [], total: 0 }

    const result = await getFormazioneResourceApi().msfoFormazioneDerogheByCfGet(cf, getDefaultAxiosConfig())
    return {
      items: result.data || [],
      total: result.data?.length || 0,
    }
  }

  const loadDataFilter = async (request: RequestData) => {
    const { data } = await getFormazioneResourceApi().msfoFormazioneDerogheByFilterGet(
      generateRequestFilter(request.filters),
      request.pageSize,
      request.currentPage,
      generateRequestSort(request.sortColumn, request.sortDirection),
      getDefaultAxiosConfig(),
    )
    return {
      items: data.items || [],
      total: data.count || 0,
    }
  }

  const filters: any = [
    { label: 'Nome', field: 'nome', type: 'input', operator: 'LIKE' },
    { label: 'Cognome', field: 'cognome', type: 'input', operator: 'LIKE' },
    { label: 'Codice Fiscale', field: 'cf', type: 'input', initialValue: cf ?? '', operator: 'LIKE' },
    {
      label: 'Triennio destinazione',
      field: 'triennio',
      type: 'select',
      options: TRIENNI_OPTIONS,
    },
  ]

  const aggiornaDeroga = async () => {
    if (!selectedEdit) return

    setLoadingEdit(true)

    let messaggio = {
      head: 'Errore aggiornamento proroga',
      data: [],
    }

    let ok = false

    try {
      const start = new Date(editData.dataInizioDeroga)
      const end = new Date(editData.dataFineDeroga)

      if (end <= start) {
        setEditError('La data fine deve essere maggiore della data inizio')
        return
      }

      if (!editData.provvedimento || editData.provvedimento === '') {
        setEditError('Inserire il provvedimento di concessione')
        return
      }

      setEditError(null)

      const payload = {
        ...selectedEdit,
        dataInizioDeroga: editData.dataInizioDeroga,
        dataFineDeroga: editData.dataFineDeroga,
        provvedimento: editData.provvedimento,
      }

      const result = await getFormazioneResourceApi().msfoFormazioneModificaDerogaPost(payload, getDefaultAxiosConfig())

      ok = result?.data?.status === 0
      messaggio.head = result?.data?.message

      if (ok) setRefreshKey((p) => p + 1)

      setShowEditModal(false)
      setSelectedEdit(null)
    } catch (e: any) {
      messaggio.head = e?.response?.data?.message || 'Errore modifica'
    } finally {
      setLoadingEdit(false)
    }

    setResultModal({
      open: true,
      title: 'Esito modifica proroga',
      ok,
      message: messaggio,
    })
  }

  const eliminaDeroga = async () => {
    if (!selectedDeroga) return

    let messaggio = {
      head: 'Errore eliminazione proroga.',
      data: [],
    }

    let ok = false
    setLoadingDelete(true)

    try {
      const result = await getFormazioneResourceApi().msfoFormazioneEliminaDerogaPost(selectedDeroga, getDefaultAxiosConfig())

      messaggio.head = result?.data?.message || 'Eliminazione completata'

      ok = result?.data?.status === 0

      if (ok) {
        setRefreshKey((prev) => prev + 1)
      }

      setShowDeleteConfirm(false)
      setSelectedDeroga(null)
    } catch (e: any) {
      messaggio.head = e?.response?.data?.message || 'Errore durante eliminazione'
    } finally {
      setLoadingDelete(false)
    }

    setResultModal({
      open: true,
      title: 'Esito eliminazione proroga',
      ok,
      message: messaggio,
    })
  }

  const riepilogoRipartizioni = (id: number, cf?: string) => {
    if (nascondiAction) {
      props.history.push(`${PATH_TO_FO_RIEPILOGO_RIPARTIZIONI}?id=${id}&cf=${cf}`)
    } else {
      props.history.push(`${PATH_TO_BO_RIEPILOGO_RIPARTIZIONI}?id=${id}&cf=${cf}`)
    }
  }

  const renderActions = (row: any) => {
    return (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button color="primary" size="sm" onClick={() => riepilogoRipartizioni(row.id, row?.cf)}>
          Visualizza ripartizioni
        </Button>

        <Button
          color="primary"
          size="sm"
          hidden={isUser}
          onClick={() => {
            setSelectedEdit(row)
            setEditData({
              dataInizioDeroga: row.dataInizioDeroga,
              dataFineDeroga: row.dataFineDeroga,
              provvedimento: row.provvedimento,
            })
            setShowEditModal(true)
          }}
        >
          Modifica proroga
        </Button>

        <Button
          color="danger"
          size="sm"
          onClick={() => {
            setSelectedDeroga(row)
            setDeleteMode('confirm')
            setProvvedimentoText('')
            setShowDeleteConfirm(true)
          }}
          hidden={isUser}
        >
          Elimina proroga
        </Button>
      </div>
    )
  }

  return (
    <PageSection>
      <div className="container register-container">
        {!nascondiAction && <GenericBreadCrumb paths={breadcrumbOptions} />}

        <Spacer />

        <Title headingLevel="h1" size="lg">
          Lista proroghe
        </Title>

        <Spacer />

        <GenericAsyncTableData
          key={refreshKey}
          tableKey="lista-proroghe"
          emptyMessage="Nessuna proroga presente"
          getRows={cf ? loadData : loadDataFilter}
          filters={cf ? [] : filters}
          columns={[
            {
              label: 'Nome',
              field: 'nome',
              isSortable: true,
            },
            {
              label: 'Cognome',
              field: 'cognome',
              isSortable: true,
            },
            {
              label: 'Codice Fiscale',
              field: 'cf',
              isSortable: true,
            },
            {
              label: 'Triennio Destinazione',
              field: 'triennio',
              isSortable: false,
              render: (row: any) => getTriennioLabel(row.triennio),
            },
            {
              label: 'Inizio Proroga',
              field: 'dataInizioDeroga',
              isSortable: false,
              render: (row: any) => formatDate(row.dataInizioDeroga),
            },
            {
              label: 'Fine Proroga',
              field: 'dataFineDeroga',
              isSortable: false,
              render: (row: any) => formatDate(row.dataFineDeroga),
            },
            {
              label: 'Data Inserimento',
              field: 'dataInserimento',
              isSortable: false,
              render: (row: any) => formatDate(row.dataInserimento.split('T')[0]),
            },
            {
              label: 'Provvedimento Di Concessione',
              field: 'provvedimento',
              isSortable: false,
            },
            {
              label: 'Crediti Spostati',
              field: 'creditiSpostati',
              isSortable: false,
              render: (row) => Number(row.creditiSpostati || 0).toFixed(3),
            },
            {
              label: '',
              field: 'actions',
              isSortable: false,
              render: renderActions,
            },
          ]}
        />
      </div>

      <Modal fade centered isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
        <ModalHeader toggle={() => setShowEditModal(false)}>Modifica proroga</ModalHeader>

        <ModalBody>
          <div className="row">
            <div className="col-6">
              <GenericInput
                id="dataInizioDeroga"
                type="date"
                label="Data inizio proroga *"
                value={editData.dataInizioDeroga}
                onChange={(cod, val) => {
                  setEditData((prev) => ({
                    ...prev,
                    [cod]: val,
                  }))

                  setEditError(null)
                }}
                errorMessage={null}
                onKeyDown={() => {}}
              />
            </div>

            <div className="col-6">
              <GenericInput
                id="dataFineDeroga"
                type="date"
                label="Data fine proroga *"
                value={editData.dataFineDeroga}
                onChange={(cod, val) => {
                  setEditData((prev) => ({
                    ...prev,
                    [cod]: val,
                  }))

                  setEditError(null)
                }}
                errorMessage={null}
                onKeyDown={() => {}}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <GenericInput
                id="provvedimento"
                type="text"
                label="Provvedimento di concessione *"
                value={editData.provvedimento}
                onChange={(cod, val) => {
                  setEditData((prev) => ({
                    ...prev,
                    [cod]: val,
                  }))

                  setEditError(null)
                }}
                errorMessage={null}
                onKeyDown={() => {}}
              />
            </div>
          </div>
          {editError && <div style={{ color: 'red', marginTop: '10px' }}>{editError}</div>}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEditModal(false)}>
            Annulla
          </Button>

          <Button color="primary" onClick={aggiornaDeroga} disabled={loadingEdit || isEditInvalid}>
            {loadingEdit ? <span className="spinner-border spinner-border-sm" /> : 'Salva'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal fade centered isOpen={showDeleteConfirm} toggle={() => setShowDeleteConfirm(false)}>
        <ModalHeader toggle={() => setShowDeleteConfirm(false)}>Conferma eliminazione</ModalHeader>

        <ModalBody>
          {deleteMode === 'confirm' && (
            <>
              Sei sicuro di voler eliminare la proroga del CF: <strong>{selectedDeroga?.cf}</strong>?
            </>
          )}

          {deleteMode === 'provvedimento' && (
            <>
              <GenericInput
                id="provvedimentoDelete"
                type="text"
                label="Provvedimento"
                value={provvedimentoText}
                onChange={(cod, val) => setProvvedimentoText(val)}
                errorMessage={null}
                onKeyDown={() => {}}
              />
              <p>Inserisci il provvedimento prima di eliminare</p>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Annulla
          </Button>

          {deleteMode === 'confirm' && (
            <>
              <Button color="danger" onClick={eliminaDeroga} disabled={loadingDelete}>
                {loadingDelete ? <span className="spinner-border spinner-border-sm" /> : 'Elimina per errore'}
              </Button>

              <Button color="danger" onClick={() => setDeleteMode('provvedimento')}>
                Elimina con provvedimento
              </Button>
            </>
          )}

          {deleteMode === 'provvedimento' && (
            <Button
              color="primary"
              disabled={!provvedimentoText || loadingDelete}
              onClick={async () => {
                await eliminaDeroga()
              }}
            >
              Conferma
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {resultModal.open && (
        <Modal fade centered isOpen={resultModal.open} toggle={() => setResultModal({ open: false })}>
          <ModalHeader style={{ fontSize: '25px' }}>{resultModal.title}</ModalHeader>

          <ModalBody>
            <p>{resultModal.message.head}</p>

            <Spacer />

            {resultModal.message.data.map((dato: any, i: number) => (
              <div key={i}>
                <p>{dato}</p>
                <Spacer />
              </div>
            ))}
          </ModalBody>

          <ModalFooter>
            <Button color="primary" onClick={() => setResultModal({ open: false })}>
              OK
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </PageSection>
  )
}

export default ListaProroghe
